import { BarChart3, CheckCircle2, Flame, Trophy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import type { SurveyTodayData } from '@/hooks/useSurvey'

interface DailySurveyProps {
  isOpen: boolean
  onClose: () => void
  surveyData: SurveyTodayData | null
  loading: boolean
  onSubmit: (optionValue: string) => Promise<void>
}

export function DailySurvey({ isOpen, onClose, surveyData, loading, onSubmit }: DailySurveyProps) {
  const [selectedAnswer, setSelectedAnswer] = useState<string>('')
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showStreakCelebration, setShowStreakCelebration] = useState(false)

  // Sync hasSubmitted with surveyData.hasRespondedToday when modal opens
  useEffect(() => {
    if (isOpen && surveyData) {
      setHasSubmitted(surveyData.hasRespondedToday)
    }
  }, [isOpen, surveyData])

  const handleSubmit = async () => {
    if (!selectedAnswer) return

    setIsSubmitting(true)
    await onSubmit(selectedAnswer)
    setIsSubmitting(false)
    setHasSubmitted(true)
    setShowStreakCelebration(true)
    setTimeout(() => setShowStreakCelebration(false), 2000)
  }

  const handleClose = () => {
    setSelectedAnswer('')
    onClose()
  }

  const currentStreak = surveyData?.currentStreak ?? 0
  const longestStreak = surveyData?.longestStreak ?? 0
  const hasRespondedToday = surveyData?.hasRespondedToday ?? false

  // Find the max percentage for scaling
  const maxPercentage = surveyData ? Math.max(...surveyData.results.map((r) => r.percentage), 1) : 1

  // Calculate new streak for display after submission
  const displayStreak = hasSubmitted && !hasRespondedToday ? currentStreak + 1 : currentStreak

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2 text-primary">
              <BarChart3 className="w-5 h-5" />
              Daily Survey
            </span>
            {displayStreak > 0 && (
              <span
                className={`flex items-center gap-1 text-sm font-medium px-2 py-1 rounded-full ${
                  showStreakCelebration
                    ? 'bg-orange-100 text-orange-600 animate-pulse'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                <Flame className={`w-4 h-4 ${displayStreak >= 7 ? 'text-orange-500' : ''}`} />
                {displayStreak} day{displayStreak !== 1 ? 's' : ''}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="py-2">
          {loading && !surveyData ? (
            // Loading skeleton
            <div className="space-y-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-3/4" />
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="flex items-center space-x-3">
                    <div className="w-4 h-4 rounded-full bg-muted" />
                    <div className="h-4 bg-muted rounded flex-1" />
                  </div>
                ))}
              </div>
              <div className="h-10 bg-muted rounded w-full" />
            </div>
          ) : !hasSubmitted ? (
            // Question View
            <div className="space-y-5">
              {currentStreak > 0 && (
                <Card className="p-3 bg-linear-to-r from-orange-50 to-amber-50 border-orange-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Flame className="w-5 h-5 text-orange-500" />
                      <span className="text-sm font-medium text-orange-700">
                        {currentStreak} day streak!
                      </span>
                    </div>
                    <span className="text-xs text-orange-600">Keep it going 🔥</span>
                  </div>
                </Card>
              )}

              <p className="text-base font-medium text-foreground leading-relaxed">
                {surveyData?.question}
              </p>

              <RadioGroup
                value={selectedAnswer}
                onValueChange={setSelectedAnswer}
                className="space-y-3"
              >
                {surveyData?.options.map((option) => (
                  <div key={option.optionValue} className="flex items-center space-x-3">
                    <RadioGroupItem
                      value={option.optionValue}
                      id={option.optionValue}
                      className="border-primary"
                    />
                    <Label
                      htmlFor={option.optionValue}
                      className="text-sm font-normal cursor-pointer flex-1"
                    >
                      {option.optionLabel}
                    </Label>
                  </div>
                ))}
              </RadioGroup>

              <Button
                onClick={handleSubmit}
                disabled={!selectedAnswer || isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Response'}
              </Button>

              <p className="text-xs text-muted-foreground text-center">
                Your identity is kept private, only totals are shown.
              </p>
            </div>
          ) : (
            // Results View
            <div className="space-y-5">
              {showStreakCelebration && displayStreak > 0 && (
                <Card className="p-4 bg-linear-to-r from-orange-100 to-amber-100 border-orange-300 animate-in fade-in zoom-in duration-300">
                  <div className="flex items-center justify-center gap-3">
                    <Flame className="w-6 h-6 text-orange-500" />
                    <div className="text-center">
                      <p className="font-semibold text-orange-700">
                        {displayStreak} Day Streak! 🎉
                      </p>
                      {displayStreak >= longestStreak && displayStreak > 1 && (
                        <p className="text-xs text-orange-600 flex items-center justify-center gap-1 mt-1">
                          <Trophy className="w-3 h-3" />
                          New personal best!
                        </p>
                      )}
                    </div>
                    <Flame className="w-6 h-6 text-orange-500" />
                  </div>
                </Card>
              )}

              <div className="flex items-center gap-2 text-accent">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Thanks for your response!</span>
              </div>

              <p className="text-sm text-muted-foreground">{surveyData?.question}</p>

              <div className="space-y-3">
                {surveyData?.results.map((result) => {
                  const isSelected = result.optionValue === selectedAnswer
                  return (
                    <div key={result.optionValue} className="space-y-1.5">
                      <div className="flex justify-between text-sm">
                        <span
                          className={isSelected ? 'font-medium text-primary' : 'text-foreground'}
                        >
                          {result.optionLabel}
                          {isSelected && ' ✓'}
                        </span>
                        <span className="font-medium text-foreground">{result.percentage}%</span>
                      </div>
                      <div className="h-3 bg-muted rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            isSelected ? 'bg-primary' : 'bg-primary/40'
                          }`}
                          style={{ width: `${(result.percentage / maxPercentage) * 100}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">{result.votes} responses</p>
                    </div>
                  )
                })}
              </div>

              <Card className="p-3 bg-muted/50">
                <div className="flex justify-between items-center">
                  <p className="text-xs text-muted-foreground">
                    Total:{' '}
                    <span className="font-medium text-foreground">
                      {surveyData?.totalVotes ?? 0}
                    </span>{' '}
                    members
                  </p>
                  {longestStreak > 0 && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Trophy className="w-3 h-3" />
                      Best: {longestStreak} {longestStreak === 1 ? 'day' : 'days'}
                    </p>
                  )}
                </div>
              </Card>

              <Button variant="outline" onClick={handleClose} className="w-full">
                Close
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
